<template>
  <div
    v-if="isFilled"
    class="ui-image-cropper"
    :class="imageClasses"
    :style="imageStyles"
    @click="handleFullScreen"
  >
    <div v-if="deleteMode" class="ui-image-cropper__confirm" @click.stop>
      <span>Are you sure you want to delete this image?</span>
      <div class="ui-image-cropper__confirm-buttons">
        <UIButton
          label="Yes"
          variant="danger"
          size="xs"
          @click="handleDeleteConfirm"
        />
        <UIButton
          label="No"
          variant="secondary"
          size="xs"
          @click="handleDeleteCancel"
        />
      </div>
    </div>
    <template v-else>
      <div class="ui-image-cropper__buttons ui-image-cropper__buttons--left">
        <UIButton :leading="ArrowsPointingOutIcon" size="xs" fill="light" />
      </div>
      <div
        v-if="!isActionsDisabled"
        class="ui-image-cropper__buttons ui-image-cropper__buttons--right"
        @click.stop
      >
        <UIButton
          :leading="ArrowUpTrayIcon"
          size="xs"
          fill="light"
          @click="handleUpload"
        />
        <UIButton
          :leading="TrashIcon"
          size="xs"
          fill="light"
          @click="handleDelete"
        />
      </div>
      <input
        ref="inputRef"
        type="file"
        accept="image/*"
        class="ui-image-cropper__input"
        @change="handleChange"
        @click.stop
      />
    </template>
  </div>
  <label v-else-if="!isActionsDisabled" class="ui-image-cropper ui-image-cropper--empty">
    <input
      ref="inputRef"
      type="file"
      accept="image/*"
      class="ui-image-cropper__input"
      @change="handleChange"
    />
    <span class="ui-image-cropper__label">
      <PhotoIcon class="ui-image-cropper__label-icon" aria-hidden="true" />
      <span class="ui-image-cropper__label-text">Click to upload an image</span>
    </span>
  </label>
</template>

<script setup lang="ts">
import { computed, markRaw, ref, useTemplateRef, watchEffect } from 'vue'

import { useModalsStore } from '@/store/modals'

import ImageCropperModal from './ImageCropperModal.vue'
import ImageCropperPreview from './ImageCropperPreview.vue'
import { UIButton } from '@ui/buttons'
import {
  TrashIcon,
  PhotoIcon,
  ArrowUpTrayIcon,
} from '@heroicons/vue/24/outline'
import { ArrowsPointingOutIcon } from '@heroicons/vue/20/solid'

type Props = {
  displayAspectRatio?: number
  cropAspectRatio?: number
  loading?: boolean
  title?: string
  disableActions?: boolean
  readonly?: boolean
}

const {
  displayAspectRatio = 16 / 9,
  cropAspectRatio = 16 / 9,
  loading,
  title,
  disableActions,
  readonly,
} = defineProps<Props>()

const modelValue = defineModel<File | string>()

const modalsStore = useModalsStore()

const inputRef = useTemplateRef<HTMLInputElement>('inputRef')

const image = ref()
const deleteMode = ref(false)

const isActionsDisabled = computed(() => readonly || disableActions)

const imageStyles = computed(() => {
  return {
    ...(image.value ? { backgroundImage: `url(${image.value})` } : {}),
    ...(displayAspectRatio ? { aspectRatio: displayAspectRatio } : {}),
  }
})

const imageClasses = computed(() => {
  return {
    'ui-image-cropper--loading': loading,
  }
})

const isFilled = computed(() => !!image.value)

const handleChange = (event: Event) => {
  const inputFile = (event.target as HTMLInputElement).files?.[0]
  if (!inputFile) return
  const reader = new FileReader()
  const fileName = inputFile.name.split('.').slice(0, -1).join('.')
  reader.addEventListener('load', ({ target }) => {
    const modalInstance = modalsStore.init(
      'image-cropper',
      markRaw(ImageCropperModal),
    )
    modalInstance?.open(modalsStore.getZIndex(), {
      image: String(target?.result),
      aspectRatio: cropAspectRatio,
      fileName,
      onApply: (base64: string, _: Blob, file: File | null) => {
        if (!file) return
        image.value = base64
        modelValue.value = file
      },
    })
  })
  reader.readAsDataURL(inputFile)
}

const handleFullScreen = () => {
  const modalInstance = modalsStore.init(
    'image-preview',
    markRaw(ImageCropperPreview),
  )
  modalInstance?.open(modalsStore.getZIndex(), {
    image: image.value,
    aspectRatio: cropAspectRatio,
    title,
  })
}

const handleUpload = () => {
  inputRef.value?.click()
}

const handleDelete = () => {
  deleteMode.value = true
}

const handleDeleteCancel = () => {
  deleteMode.value = false
}

const handleDeleteConfirm = async () => {
  deleteMode.value = false
  image.value = undefined
  modelValue.value = undefined
  inputRef.value && (inputRef.value.value = '')
}

watchEffect(() => {
  image.value = modelValue.value
})
</script>

<style>
.ui-image-cropper {
  @apply min-h-20 max-w-full;
  @apply flex items-center justify-center;
  @apply relative;
  @apply bg-body-dark bg-cover bg-center bg-no-repeat;
  @apply text-light-text;
  @apply cursor-pointer;

  &--loading {
    @apply blur-md;
  }

  &--empty {
    @apply h-8;
    @apply aspect-auto;
    @apply hover:text-primary;
  }

  &__label {
    @apply flex items-center gap-1;
    @apply text-base sm:text-sm;
  }

  &__label-icon {
    @apply w-4 h-4;
  }

  &__input {
    @apply invisible absolute inset-0;
  }

  &__buttons {
    @apply absolute top-2;
    @apply hidden items-center gap-1;
    @apply p-0.5;
    @apply bg-white dark:bg-gray-700 !bg-opacity-75;
    @apply rounded-md;

    &--left {
      @apply left-2;
    }

    &--right {
      @apply right-2;
    }

    .ui-button {
      @apply dark:text-indigo-300;
    }
  }

  &:hover &__buttons {
    @apply flex;
  }

  &__confirm {
    @apply absolute inset-0;
    @apply flex flex-col items-center;
    @apply gap-4 p-2;
    @apply text-common-text;
    @apply bg-white dark:bg-gray-700 !bg-opacity-75;
    @apply z-30;

    .ui-button {
      @apply min-w-[3rem];
    }
  }

  &__confirm-buttons {
    @apply flex justify-center;
    @apply gap-2;
  }
}
</style>
